import {
  Column,
  Modal,
  Select,
  Switch,
  Table,
} from "@appkit4/react-components";
import { useEffect, useRef, useState } from "react";
import { Button } from "../../components/ReadonlyAwareInputs";
import { getGroupData } from "../../services/GroupContext";
import { formatDate } from "../../utils/dateProcessor";
import { formatReturnStatus, getPeriodsSplit } from "./GroupPoAUtils";

import "./groupPoa.scss";

const CenteredBox = ({ children }) => (
  <div style={{ textAlign: "center" }}>{children}</div>
);

const AdminOpenPeriodsModal = ({
  visible,
  setVisible,
  tableData,
  updateTableData,
}) => {
  const [periodOptions, setPeriodOptions] = useState([]);
  const [revisionFlags, setRevisionFlags] = useState([]);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(null);
  const [periodsToReopen, setPeriodsToReopen] = useState([]);
  const [periodsToSkip, setPeriodsToSkip] = useState([]);
  const [periodsToReopenWithIndexes, setPeriodsToReopenWithIndexes] = useState(
    [],
  );
  const [lockIndex, setLockIndex] = useState(null);
  const editedIndexes = useRef(new Set());
  const initialFlags = useRef([]);

  useEffect(() => {
    const { periods_of_account } = tableData
      ? { periods_of_account: tableData }
      : getGroupData();
    const flags = [];

    const periods =
      periods_of_account?.map((period, index) => {
        const { return_status } = period ?? {};
        const { isFinalised, isSubmitted, isRevised } = return_status ?? {};
        flags.push({ isFinalised, isSubmitted, isRevised });

        return {
          value: index,
          label: `${formatDate(period?.period_start)} - ${formatDate(period?.period_end)}`,
        };
      }) ?? [];

    setPeriodOptions(periods);
    initialFlags.current = flags;
    setSelectedPeriodIndex(null);
  }, [visible]);

  useEffect(() => {
    if (selectedPeriodIndex === null) return;

    const { periodsToSkip, periodsToReopen } = getPeriodsSplit(
      tableData,
      selectedPeriodIndex,
    );

    setPeriodsToReopenWithIndexes(
      periodsToReopen?.map((period, index) => ({ ...period, rowIndex: index })),
    );
    setPeriodsToReopen(periodsToReopen);
    setPeriodsToSkip(periodsToSkip);
    setRevisionFlags(initialFlags.current.slice(selectedPeriodIndex));
    setLockIndex(null);
    editedIndexes.current = new Set();
  }, [selectedPeriodIndex]);

  const updateRevisionFlags = (index, flagType, value) => {
    const flagsBeforeIndex = revisionFlags.slice(0, index);
    let flagsAfterIndex = revisionFlags.slice(index + 1);
    const flagAtIndex = revisionFlags[index] ?? {};

    let updatedFlag = {
      ...flagAtIndex,
      [flagType]: value,
    };

    editedIndexes.current.add(index);

    const currentReturnStatus = periodsToReopen[index]?.return_status ?? {};
    if (flagType === "isFinalised") {
      if (value === true && index === lockIndex) {
        setLockIndex(null);
      } else if (value === false) {
        flagsAfterIndex = flagsAfterIndex.map((flag) => ({
          ...flag,
          isFinalised: false,
          isSubmitted: false,
        }));

        updatedFlag = {
          ...updatedFlag,
          isFinalised: false,
          isSubmitted: false,
        };

        for (let i = index + 1; i < periodsToReopen.length; i++) {
          editedIndexes.current.add(i);
        }

        setLockIndex(index);
      }
    }

    setRevisionFlags([...flagsBeforeIndex, updatedFlag, ...flagsAfterIndex]);
  };

  const isLocked = (index) => {
    const isPrevPeriodFinalised = revisionFlags[index - 1]?.isFinalised;
    const isFinalisationLocked = lockIndex === null ? false : index > lockIndex;

    return (index > 0 && !isPrevPeriodFinalised) || isFinalisationLocked;
  };

  const handleCancel = () => {
    setPeriodOptions([]);
    setRevisionFlags([]);
    initialFlags.current = [];
    setSelectedPeriodIndex(null);
    setLockIndex(null);
    setPeriodsToReopen([]);
    setPeriodsToSkip([]);
    setVisible(false);
  };

  const reopen = () => {
    const editedPeriodIndexes = [...editedIndexes.current];

    if (editedPeriodIndexes.length === 0) {
      handleCancel();
      return;
    }

    editedPeriodIndexes.forEach((index) => {
      const period = periodsToReopen[index];

      period.return_status = {
        ...period?.return_status,
        ...revisionFlags[index],
      };
    });

    updateTableData([...periodsToSkip, ...periodsToReopen]);
    handleCancel();
  };

  const getYesNo = (value) => (value ? "Yes" : "No");

  return (
    <Modal
      visible={visible}
      initialFocus={false}
      title={"Open period(s) as Admin"}
      ariaLabel={"Open period(s) as Admin"}
      maskCloseable={false}
      onCancel={handleCancel}
      modalStyle={{ width: "110rem" }}
      footerStyle={{ paddingTop: "8px", height: "64px" }}
      footer={
        <>
          <Button neverReadonly kind="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button kind="primary" onClick={reopen}>
            Open Period(s)
          </Button>
        </>
      }
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr max-content",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Select
          searchable
          dropdownRenderMode="portal"
          data={periodOptions}
          placeholder="Select a Period to Open"
          value={selectedPeriodIndex}
          onSelect={(value) => setSelectedPeriodIndex(value)}
        />
        <Button
          kind="primary"
          style={{ height: "100%", display: "flex", alignItems: "center" }}
          onClick={() => {
            setRevisionFlags(initialFlags.current.slice(selectedPeriodIndex));
            setLockIndex(null);
          }}
        >
          Reset
        </Button>
      </div>

      {periodsToReopen.length > 0 && (
        <Table
          style={{ marginTop: "16px" }}
          originalData={periodsToReopenWithIndexes}
          hasTitle
          striped
        >
          <Column
            field="period_start"
            renderCell={(period) => formatDate(period?.period_start)}
          >
            Period Start
          </Column>
          <Column
            field="period_end"
            renderCell={(period) => formatDate(period?.period_end)}
          >
            Period End
          </Column>
          <Column
            field="return_status"
            renderCell={(period) => formatReturnStatus(period)}
          >
            Return Status
          </Column>
          <Column
            field="return_status"
            renderCell={({ return_status }) => (
              <CenteredBox>{getYesNo(return_status?.isFinalised)}</CenteredBox>
            )}
          >
            Is Period Finalised?
          </Column>
          <Column
            field="rowIndex"
            renderCell={(period) => (
              <Switch
                className="horizontal-center"
                checked={Boolean(revisionFlags[period.rowIndex]?.isFinalised)}
                disabled={isLocked(period.rowIndex)}
                onChange={(value) =>
                  updateRevisionFlags(period.rowIndex, "isFinalised", value)
                }
              />
            )}
          >
            Should Period be Finalised?
          </Column>
          <Column
            field="return_status"
            renderCell={({ return_status }) => (
              <CenteredBox>{getYesNo(return_status?.isSubmitted)}</CenteredBox>
            )}
          >
            Is Period Submitted?
          </Column>
          <Column
            field="rowIndex"
            renderCell={(period) => (
              <Switch
                className="horizontal-center"
                checked={Boolean(revisionFlags[period.rowIndex]?.isSubmitted)}
                disabled={
                  isLocked(period.rowIndex) ||
                  !revisionFlags[period.rowIndex]?.isFinalised
                }
                onChange={(value) =>
                  updateRevisionFlags(period.rowIndex, "isSubmitted", value)
                }
              />
            )}
          >
            Should Period be Submitted?
          </Column>
          <Column
            field="return_status"
            renderCell={({ return_status }) => (
              <CenteredBox>{getYesNo(return_status?.isRevised)}</CenteredBox>
            )}
          >
            Is Period Revised?
          </Column>
          <Column
            field="rowIndex"
            renderCell={(period) => (
              <Switch
                className="horizontal-center"
                checked={Boolean(revisionFlags[period.rowIndex]?.isRevised)}
                onChange={(value) =>
                  updateRevisionFlags(period.rowIndex, "isRevised", value)
                }
              />
            )}
          >
            Should Period be Revised?
          </Column>
        </Table>
      )}
    </Modal>
  );
};

export default AdminOpenPeriodsModal;
